<script setup lang="ts">
import {
  ButtonIcon,
  CustomLink,
  LinkColor,
  LinkIcon,
  LinkIconLocation,
  LinkSize,
  createSnackbarNotification,
  Modal
} from "@frontiers-vue3/public-pages";
import type { IRTSidePanel } from "./IRTSidePanel";
import type { IJournal } from "~/types";
import { useShareInSocialNetwork } from "~/composables/useShareInSocialNetwork";
import { useClipboard } from "@vueuse/core";
import RTSidePanelImpact from "./components/RTSidePanelImpact/RTSidePanelImpact.vue";
import RTSidepanelParticipatingJournals from "./components/RTSidepanelParticipatingJournals/RTSidepanelParticipatingJournals.vue";
import RTSidePanelSubmitManuscript from "./components/RTSidePanelSubmitManuscript/RTSidePanelSubmitManuscript.vue";
import QRCode from "qrcode";

const { copy } = useClipboard();
const { getSocialShareLink } = useShareInSocialNetwork();
const props = defineProps<IRTSidePanel>();

const config = useRuntimeConfig();
const mainStore = useMain();
const { isImpactMetricsVisible, showRtImpactTab, minimumRtDownloadsCount } =
  useEnvironmentConfigStore().getConfig();

const shareUrl = getSocialShareLink("WeChat");
const showWeChatModal = ref(false);
const qrCodeCanvas = ref<HTMLCanvasElement | null>(null);

const sanitize = inject("sanitize") as (html: string) => string;

const participatingJournals = ref<IJournal[]>(
  mainStore.currentResearchTopic?.journals || []
);
const displayParticipatingJournals = ref<boolean>(
  useEnvBoolean(config.public.isParticipatingJournalsVisible) &&
    participatingJournals.value.length > 0
);

const rtSponsorship = mainStore.rtSponsorship;

const showSponsorshipBlock = computed(
  (): boolean =>
    !!(
      Boolean(config.public.showRtSponsorship) &&
      rtSponsorship &&
      Object.values(rtSponsorship).length > 0
    )
);

const displayImpactSection = computed((): boolean => {
  return (
    isImpactMetricsVisible &&
    showRtImpactTab &&
    Object.values(props.impactNumbers).some(
      (impactNumber) => impactNumber > minimumRtDownloadsCount
    )
  );
});

const hideImage = (e: Event): void => {
  if (!e.target) return;
  (e.target as HTMLElement).setAttribute("style", "display: none");
};

const shareWeChatQR = async () => {
  showWeChatModal.value = !showWeChatModal.value;

  await nextTick();

  if (qrCodeCanvas.value) {
    await QRCode.toCanvas(qrCodeCanvas.value, shareUrl, { width: 200 });
  }
};

const shareWeChat = async () => {
  if (typeof window === "undefined") return;
  if (window.innerWidth > 1024) {
    await shareWeChatQR();
  } else {
    copy(window.location.href);

    createSnackbarNotification({
      message: "Link copied. Share the link on WeChat!",
      type: "success"
    });
  }
};
</script>
<template>
  <div class="RtSidePanel">
    <div class="RtSidePanel__Section RtSidePanel__Section--ShareIcons">
      <span>Share on</span>
      <div>
        <ButtonIcon
          label="Share on X"
          show-tooltip
          icon="TwitterX"
          :href="getSocialShareLink('X')"
          target="_blank">
        </ButtonIcon>
        <ButtonIcon
          label="Share on LinkedIn"
          show-tooltip
          icon="Linkedin"
          :href="getSocialShareLink('LinkedIn')"
          target="_blank" />
        <ButtonIcon
          label="Share on Facebook"
          show-tooltip
          icon="Facebook"
          :href="getSocialShareLink('Facebook')"
          target="_blank" />
        <ButtonIcon
          label="Share on WeChat"
          show-tooltip
          icon="WeChat"
          @click="shareWeChat">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="128"
            height="128"
            viewBox="0 0 128 128"
            fill="none">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M112.688 102.779C120.477 97.1419 125.5 88.7369 125.5 79.4606C125.5 62.3944 108.895 48.6081 88.4463 48.6081C67.9975 48.6081 51.3925 62.4456 51.3925 79.4606C51.3925 96.4756 67.9975 110.313 88.4463 110.313C92.7 110.313 96.7487 109.698 100.541 108.622L101.618 108.468C102.335 108.468 102.95 108.673 103.565 109.032L111.662 113.696L112.38 113.952C113.046 113.952 113.61 113.388 113.61 112.722L113.405 111.799L111.714 105.547L111.611 104.778C111.611 103.958 112.021 103.189 112.636 102.779H112.688ZM46.985 14.4756C22.385 14.4756 2.5 31.0806 2.5 51.5294C2.5 62.7019 8.49625 72.7469 17.875 79.5119C18.6438 80.0244 19.105 80.9469 19.105 81.9206L18.9513 82.8431L16.9525 90.3256L16.6962 91.4019C16.6962 92.2219 17.3625 92.8881 18.1825 92.8881L19.0538 92.6319L28.7913 86.9944C29.5088 86.5844 30.2775 86.3281 31.1488 86.3281L32.4812 86.5331C37.0425 87.8144 41.9113 88.5831 46.985 88.5831H49.445C48.4712 85.6106 47.9587 82.5869 47.9587 79.4606C47.9587 60.8056 66.1013 45.6869 88.4975 45.6869H90.9062C87.5237 28.0056 69.125 14.4756 46.985 14.4756ZM76.095 74.4894C73.3787 74.4894 71.175 72.2856 71.175 69.5694C71.175 66.8531 73.3787 64.6494 76.095 64.6494C78.8112 64.6494 81.015 66.8531 81.015 69.5694C81.015 72.2856 78.8112 74.4894 76.095 74.4894ZM100.798 74.4894C98.0813 74.4894 95.8775 72.2856 95.8775 69.5694C95.8775 66.8531 98.0813 64.6494 100.798 64.6494C103.514 64.6494 105.717 66.8531 105.717 69.5694C105.717 72.2856 103.514 74.4894 100.798 74.4894ZM32.1225 45.5844C28.8425 45.5844 26.1775 42.9194 26.1775 39.6394C26.1775 36.3594 28.8425 33.6944 32.1225 33.6944C35.4025 33.6944 38.0675 36.3594 38.0675 39.6394C38.0675 42.9194 35.4025 45.5844 32.1225 45.5844ZM61.7962 45.5844C58.5162 45.5844 55.8512 42.9194 55.8512 39.6394C55.8512 36.3594 58.5162 33.6944 61.7962 33.6944C65.0762 33.6944 67.7412 36.3594 67.7412 39.6394C67.7412 42.9194 65.0762 45.5844 61.7962 45.5844Z"
              fill="#282828" />
          </svg>
        </ButtonIcon>
      </div>
    </div>
    <!-- Participating journals -->
    <RTSidepanelParticipatingJournals
      v-if="displayParticipatingJournals"
      :journals="participatingJournals" />

    <!-- Submit -->
    <RTSidePanelSubmitManuscript v-if="isSubmitManuscriptEnabled" />

    <!-- Sponsorship -->
    <section
      v-if="showSponsorshipBlock"
      class="RtSidePanel__Section Sponsorship">
      <p class="h4">Sponsorship</p>

      <p
        v-if="rtSponsorship && rtSponsorship.message"
        class="Sponsorship__Content"
        v-html="sanitize(rtSponsorship.message)"></p>
      <div>
        <img
          v-if="rtSponsorship?.imageUrl"
          class="Sponsorship__Image"
          :src="rtSponsorship?.imageUrl"
          @error="hideImage" />

        <CustomLink
          v-if="rtSponsorship?.link"
          class="Sponsorship__Link"
          :link="{
            text: 'Search for my institution',
            url: rtSponsorship?.link,
            target: '_blank'
          }"
          :size="LinkSize.Medium"
          :color="LinkColor.Maincolor"
          :icon="LinkIcon.ChevronRight"
          :icon-location="LinkIconLocation.Right" />
      </div>
    </section>

    <!-- Impact -->
    <RTSidePanelImpact
      v-if="displayImpactSection"
      :impact-numbers="impactNumbers" />
  </div>
  <Modal
    class="WeChatModal"
    title="Share on WeChat"
    heading="p"
    :force-show="showWeChatModal">
    <template #body>
      <div class="WeChatModal__message">
        <p>Scan with WeChat to share this article</p>
        <canvas ref="qrCodeCanvas" width="200" height="200"></canvas>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss">
.RtSidePanel {
  background-color: $grey10;
  width: var(--layout-col-5);
  margin-left: 0;

  @media #{$break-md} {
    width: 100%;
    margin-bottom: 64px;
  }

  @media #{$break-md} {
    margin-top: 48px;
  }

  @media #{$break-xs} {
    margin: 24px 0;
  }

  &__Section {
    position: relative;
    padding: 24px 0;
    border-bottom: 1px solid $grey40;
    color: $grey70;

    &:first-child {
      border-top: 1px solid $grey40;
    }

    &__Description {
      @include f-16-300;

      margin-top: 8px;
      margin-bottom: 24px;
    }

    &__Title {
      @include f-18;
      font-weight: 400;
      margin-top: 0;
    }

    ul {
      p {
        @include f-16;
        font-weight: 400;
        margin-bottom: 0;
      }
    }

    &__AuthorList {
      margin-top: 16px;

      li {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        img {
          height: 32px;
          width: 32px;
          border-radius: 40px;
          overflow: hidden;
        }
      }
    }

    &__Link {
      @include f-16;
      font-weight: 400;
      position: absolute;
      bottom: -10px;
      right: 0;
      padding-left: 16px;
      color: var(--maincolor50);
      background-color: $grey10;
    }

    &:has(.RtSidePanel__Section__Link) {
      padding-bottom: 33px;
    }

    &--ShareIcons {
      @include f-18-500;
      display: flex;
      align-items: center;
      gap: 20px;

      @media #{$break-laptop-sm} {
        gap: 12px;
      }

      @media #{$break-md} {
        gap: 20px;
      }

      div {
        display: flex;
        gap: 12px;

        @media #{$break-laptop-sm} {
          gap: 2px;
        }

        @media #{$break-md} {
          gap: 12px;
        }
      }
    }
  }

  .Sponsorship {
    p {
      @include f-18-500;
      color: $grey70;
      margin: 0 0px 24px 0;

      & + .Sponsorship__Content {
        @include f-16-300;
        margin: 0 0 24px 0;
      }
    }

    img {
      border-radius: 8px;
      border: 1px solid $grey30;
      margin-bottom: 24px;
      max-inline-size: 100%;
      max-block-size: 100%;
    }

    &__Link {
      display: flex;
      @include f-16-500;
      color: var(--maincolor50);
      gap: 8px;
      width: max-content;
      svg {
        margin-top: 1px;
      }
    }
  }

  .ButtonIcon {
    svg {
      width: 24px;
      height: 24px;
      display: inline;

      * {
        fill: $grey70;
      }
    }
  }
}
.WeChatModal {
  z-index: 2000;

  &__message {
    text-align: center;
  }
}
</style>
